import { AlertTriangle, RefreshCw } from "lucide-react";
import React, { useMemo } from "react";
import { isRouteErrorResponse, useNavigate, useParams, useRouteError } from "react-router-dom";
import { Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "randmarcomps";
import Path from "../routes/Path";

const ProductError: React.FC = () => {
  const navigate = useNavigate();
  const { applicationId, randmarSKU } = useParams<{ applicationId: string; randmarSKU: string }>();
  const routeError = useRouteError();

  const errorMessage = useMemo(() => {
    if (isRouteErrorResponse(routeError)) {
      return `${routeError.status} ${routeError.statusText}`;
    }
    if (routeError instanceof Error) {
      return routeError.message;
    }
    return "We hit an unexpected issue loading this product.";
  }, [routeError]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card className="w-full max-w-xl border border-[var(--ops-border)] shadow-[var(--shadow-card)]">
        <CardHeader className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            Product page error
          </div>
          <CardTitle>We couldn&apos;t load this product</CardTitle>
          <CardDescription>
            Something went wrong while rendering the Product page{randmarSKU ? ` for SKU ${randmarSKU}` : ""}. You can retry or go back to search.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
            <p className="font-semibold text-slate-800">What happened</p>
            <p className="mt-1">{errorMessage}</p>
          </div>
        </CardContent>
        <CardFooter className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button variant="outline" onClick={() => navigate(Path.search(applicationId || ""))}>
            Go to search
          </Button>
          <Button onClick={() => navigate(0)}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ProductError;

